// FinKloz — Stock Option Grants Hook
// Supabase CRUD for option_grants, option_movements, equity_audit_log

import { useState, useEffect, useCallback } from 'react';
import { getAccessTokenSync } from './useAccessToken';

const SB_URL = process.env.REACT_APP_SUPABASE_URL;
const SB_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

function headers(prefer) {
  const token = getAccessTokenSync();
  const h = {
    'apikey': SB_KEY,
    'Authorization': `Bearer ${token || SB_KEY}`,
    'Content-Type': 'application/json',
  };
  if (prefer) h['Prefer'] = prefer;
  return h;
}

// Map DB row → frontend object
function toFrontend(row) {
  return {
    id: row.id,
    name: row.employee_name,
    rel: row.relationship,
    grantDate: row.board_approval_date,
    vestStart: row.vesting_start,
    expiry: row.expiry_date,
    options: row.num_options,
    price: Number(row.exercise_price),
    currency: row.exercise_currency,
    cohort: row.cohort,
    fv: Number(row.bs_fair_value),
    vestingPaused: row.vesting_paused || false,
    vestingPauseStart: row.vesting_pause_start,
    vestingPauseEnd: row.vesting_pause_end,
    vestingPauseReason: row.vesting_pause_reason,
    status: row.status || 'final',
    documents: row.documents || [],
    notes: row.notes,
  };
}

// Map frontend object → DB row (for inserts/updates)
function toDB(grant, companyId) {
  const row = {};
  if (companyId) row.company_id = companyId;
  if (grant.name !== undefined) row.employee_name = grant.name;
  if (grant.rel !== undefined) row.relationship = grant.rel;
  if (grant.grantDate !== undefined) row.board_approval_date = grant.grantDate;
  if (grant.vestStart !== undefined) row.vesting_start = grant.vestStart;
  if (grant.expiry !== undefined) row.expiry_date = grant.expiry;
  if (grant.options !== undefined) row.num_options = grant.options;
  if (grant.price !== undefined) row.exercise_price = grant.price;
  if (grant.currency !== undefined) row.exercise_currency = grant.currency;
  if (grant.cohort !== undefined) row.cohort = grant.cohort;
  if (grant.fv !== undefined) row.bs_fair_value = grant.fv;
  if (grant.status !== undefined) row.status = grant.status;
  if (grant.documents !== undefined) row.documents = grant.documents;
  if (grant.notes !== undefined) row.notes = grant.notes;
  return row;
}

export default function useOptionGrants(companyId) {
  const [grants, setGrants] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  // ─── Fetch grants ─────────────────────────────────────────────────
  const fetchGrants = useCallback(async () => {
    if (!SB_URL || !SB_KEY || !companyId) {
      setGrants([]);
      setMovements([]);
      setLoading(false);
      return;
    }
    setGrants([]);
    setLoading(true);
    try {
      const [gRes, mRes] = await Promise.all([
        fetch(`${SB_URL}/rest/v1/option_grants?company_id=eq.${companyId}&deleted_at=is.null&order=employee_name.asc,board_approval_date.asc`, { headers: headers() }),
        fetch(`${SB_URL}/rest/v1/option_movements?company_id=eq.${companyId}&deleted_at=is.null&order=movement_date.asc`, { headers: headers() }),
      ]);
      if (!gRes.ok) throw new Error(`grants: ${gRes.status}`);
      if (!mRes.ok) throw new Error(`movements: ${mRes.status}`);
      const gRows = await gRes.json();
      const mRows = await mRes.json();
      setGrants(gRows.map(toFrontend));
      setMovements(mRows.map(r => ({
        id: r.id,
        grantId: r.grant_id,
        type: r.movement_type,
        date: r.movement_date,
        options: r.num_options,
        notes: r.notes,
      })));
    } catch (err) {
      console.error('useOptionGrants fetch error:', err);
      setGrants([]);
      setMovements([]);
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchGrants(); }, [fetchGrants]);

  // ─── Audit log helper ─────────────────────────────────────────────
  const logAudit = useCallback(async (tableName, recordId, action, fieldName, oldVal, newVal, changedBy) => {
    if (!SB_URL || !SB_KEY) return;
    try {
      await fetch(`${SB_URL}/rest/v1/equity_audit_log`, {
        method: 'POST',
        headers: headers('return=minimal'),
        body: JSON.stringify({
          company_id: companyId,
          table_name: tableName,
          record_id: recordId,
          action,
          field_name: fieldName || null,
          old_value: oldVal != null ? String(oldVal) : null,
          new_value: newVal != null ? String(newVal) : null,
          changed_by: changedBy || 'system',
          changed_at: new Date().toISOString(),
        }),
      });
    } catch (err) {
      console.error('audit log error:', err);
    }
  }, [companyId]);

  // ─── Create grant ─────────────────────────────────────────────────
  const createGrant = useCallback(async (grantData, changedBy) => {
    // Compute expiry if not set (10 years from grant date)
    let expiry = grantData.expiry;
    if (!expiry && grantData.grantDate) {
      const d = new Date(grantData.grantDate);
      d.setFullYear(d.getFullYear() + 10);
      expiry = d.toISOString().split('T')[0];
    }
    // Local-only mode (no Supabase)
    if (!SB_URL || !SB_KEY) {
      const localGrant = { ...grantData, id: `local-${Date.now()}`, expiry };
      setGrants(prev => [...prev, localGrant]);
      return localGrant;
    }
    const row = { ...toDB(grantData, companyId) };
    if (expiry) row.expiry_date = expiry;
    try {
      const res = await fetch(`${SB_URL}/rest/v1/option_grants`, {
        method: 'POST',
        headers: headers('return=representation'),
        body: JSON.stringify(row),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const [created] = await res.json();
      const newGrant = toFrontend(created);
      setGrants(prev => [...prev, newGrant]);
      logAudit('option_grants', created.id, 'create', null, null, null, changedBy);
      return newGrant;
    } catch (err) {
      console.error('createGrant error:', err);
      return null;
    }
  }, [companyId, logAudit]);

  // ─── Save (update) grant ──────────────────────────────────────────
  const saveGrant = useCallback(async (grantId, updates, changedBy) => {
    setGrants(prev => prev.map(g => g.id === grantId ? { ...g, ...updates } : g));
    if (!SB_URL || !SB_KEY) return;
    const row = toDB(updates);
    try {
      await fetch(`${SB_URL}/rest/v1/option_grants?id=eq.${grantId}`, {
        method: 'PATCH',
        headers: headers('return=minimal'),
        body: JSON.stringify(row),
      });
      for (const [key, val] of Object.entries(updates)) {
        const oldGrant = grants.find(g => g.id === grantId);
        if (oldGrant && oldGrant[key] !== val) {
          logAudit('option_grants', grantId, 'update', key, oldGrant[key], val, changedBy);
        }
      }
    } catch (err) {
      console.error('saveGrant error:', err);
    }
  }, [grants, logAudit]);

  // ─── Soft delete grant ────────────────────────────────────────────
  const deleteGrant = useCallback(async (grantId, changedBy) => {
    setGrants(prev => prev.filter(g => g.id !== grantId));
    if (!SB_URL || !SB_KEY) return;
    try {
      await fetch(`${SB_URL}/rest/v1/option_grants?id=eq.${grantId}`, {
        method: 'PATCH',
        headers: headers('return=minimal'),
        body: JSON.stringify({ deleted_at: new Date().toISOString() }),
      });
      logAudit('option_grants', grantId, 'delete', null, null, null, changedBy);
    } catch (err) {
      console.error('deleteGrant error:', err);
    }
  }, [logAudit]);

  // ─── Pause vesting ────────────────────────────────────────────────
  const pauseVesting = useCallback(async (grantId, start, end, reason, changedBy) => {
    const updates = {
      vesting_paused: true,
      vesting_pause_start: start,
      vesting_pause_end: end || null,
      vesting_pause_reason: reason || null,
    };
    try {
      await fetch(`${SB_URL}/rest/v1/option_grants?id=eq.${grantId}`, {
        method: 'PATCH',
        headers: headers('return=minimal'),
        body: JSON.stringify(updates),
      });
      setGrants(prev => prev.map(g => g.id === grantId ? {
        ...g, vestingPaused: true, vestingPauseStart: start,
        vestingPauseEnd: end || null, vestingPauseReason: reason || null,
      } : g));
      logAudit('option_grants', grantId, 'pause', 'vesting_paused', 'false', 'true', changedBy);
    } catch (err) {
      console.error('pauseVesting error:', err);
    }
  }, [logAudit]);

  // ─── Resume vesting ───────────────────────────────────────────────
  const resumeVesting = useCallback(async (grantId, changedBy) => {
    const updates = {
      vesting_paused: false,
      vesting_pause_start: null,
      vesting_pause_end: null,
      vesting_pause_reason: null,
    };
    try {
      await fetch(`${SB_URL}/rest/v1/option_grants?id=eq.${grantId}`, {
        method: 'PATCH',
        headers: headers('return=minimal'),
        body: JSON.stringify(updates),
      });
      setGrants(prev => prev.map(g => g.id === grantId ? {
        ...g, vestingPaused: false, vestingPauseStart: null,
        vestingPauseEnd: null, vestingPauseReason: null,
      } : g));
      logAudit('option_grants', grantId, 'resume', 'vesting_paused', 'true', 'false', changedBy);
    } catch (err) {
      console.error('resumeVesting error:', err);
    }
  }, [logAudit]);

  // ─── Add movement (exercise / forfeit) ────────────────────────────
  const addMovement = useCallback(async (grantId, type, date, qty, notes, changedBy) => {
    const row = {
      company_id: companyId,
      grant_id: grantId,
      movement_type: type,
      movement_date: date,
      num_options: qty,
      notes: notes || null,
    };
    try {
      const res = await fetch(`${SB_URL}/rest/v1/option_movements`, {
        method: 'POST',
        headers: headers('return=representation'),
        body: JSON.stringify(row),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const [created] = await res.json();
      setMovements(prev => [...prev, {
        id: created.id,
        grantId: created.grant_id,
        type: created.movement_type,
        date: created.movement_date,
        options: created.num_options,
        notes: created.notes,
      }]);
      logAudit('option_movements', created.id, 'create', 'movement_type', null, type, changedBy);
      return created;
    } catch (err) {
      console.error('addMovement error:', err);
      return null;
    }
  }, [companyId, logAudit]);

  return {
    grants,
    movements,
    loading,
    createGrant,
    saveGrant,
    deleteGrant,
    pauseVesting,
    resumeVesting,
    addMovement,
    refetch: fetchGrants,
  };
}
